import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, Paperclip } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { useToast } from '../hooks/use-toast';
import { companyInfo } from '../mock';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    needsAttachments: false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const serviceLabels = {
      transport: 'Transport de matières premières',
      location: "Location d'engins lourds",
      both: 'Transport + Location',
      logistique: 'Prestations logistiques clé en main',
      autre: 'Autre demande'
    };

    const subject = `Demande de devis InfraOne — ${
      serviceLabels[formData.service] || 'Non précisé'
    }`;

    const body = `Bonjour,

Je souhaite obtenir un devis pour la prestation suivante :

--- Coordonnées ---
Nom / Entreprise : ${formData.name}${
      formData.company ? ' (' + formData.company + ')' : ''
    }
Email : ${formData.email}
Téléphone : ${formData.phone || 'non communiqué'}

--- Demande ---
Type de prestation : ${serviceLabels[formData.service] || 'non précisé'}
Pièces jointes à transmettre : ${formData.needsAttachments ? 'OUI' : 'NON'}

--- Message ---
${formData.message}

Cordialement,
${formData.name}`;

    const mailtoUrl = `mailto:${companyInfo.quoteEmail}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;

    window.location.href = mailtoUrl;

    toast({
      title: 'Ouverture de votre messagerie…',
      description:
        'Votre client mail va s\'ouvrir. Si rien ne se passe, envoyez directement à ' +
        companyInfo.quoteEmail
    });
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-slate-900 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Contact & <span className="text-orange-400">Devis rapide</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Réponse sous 24h ouvrées. Disponibilité terrain 24/7 pour les
            interventions urgentes.
          </p>
        </div>
      </section>

      {/* Cartes infos */}
      <section className="py-12 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 -mt-20">
            <Card className="shadow-xl border-t-4 border-t-orange-500">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin size={28} className="text-orange-500" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Adresse
                </h3>
                <p className="text-slate-600 text-sm">{companyInfo.address}</p>
              </CardContent>
            </Card>

            <Card className="shadow-xl border-t-4 border-t-orange-500">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone size={28} className="text-orange-500" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Téléphones
                </h3>
                <div className="space-y-1">
                  <a
                    href={`tel:${companyInfo.phone.replace(/[^0-9+]/g, '')}`}
                    className="block text-slate-600 text-sm hover:text-orange-500 transition-colors"
                  >
                    {companyInfo.phone}
                  </a>
                  <a
                    href={`tel:${companyInfo.phone2.replace(/[^0-9+]/g, '')}`}
                    className="block text-slate-600 text-sm hover:text-orange-500 transition-colors"
                  >
                    {companyInfo.phone2}
                  </a>
                  <a
                    href={`tel:${companyInfo.phone3.replace(/[^0-9+]/g, '')}`}
                    className="block text-slate-600 text-sm hover:text-orange-500 transition-colors"
                  >
                    {companyInfo.phone3}
                  </a>
                  <a
                    href={`tel:${companyInfo.phoneInternational.replace(/[^0-9+]/g, '')}`}
                    className="block text-slate-600 text-sm hover:text-orange-500 transition-colors"
                  >
                    {companyInfo.phoneInternational}
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-xl border-t-4 border-t-orange-500">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail size={28} className="text-orange-500" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Email</h3>
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="text-slate-600 text-sm hover:text-orange-500 transition-colors break-all"
                >
                  {companyInfo.email}
                </a>
              </CardContent>
            </Card>

            <Card className="shadow-xl border-t-4 border-t-orange-500">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock size={28} className="text-orange-500" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Disponibilité
                </h3>
                <p className="text-slate-600 text-sm">
                  Bureau : Lun–Ven 8h–17h
                  <br />
                  Terrain : 24/7
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Formulaire + carte */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Form */}
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Demande de <span className="text-orange-500">devis rapide</span>
              </h2>
              <p className="text-slate-600 mb-8">
                Remplissez le formulaire — votre messagerie s'ouvrira pré-remplie
                pour envoyer la demande à notre équipe commerciale.
              </p>

              <form
                onSubmit={handleSubmit}
                className="space-y-6"
                data-testid="contact-form"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-slate-700 mb-2 block">
                      Nom / Entreprise *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Votre nom"
                      data-testid="contact-input-name"
                    />
                  </div>
                  <div>
                    <Label
                      htmlFor="company"
                      className="text-slate-700 mb-2 block"
                    >
                      Organisation
                    </Label>
                    <Input
                      id="company"
                      name="company"
                      type="text"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Nom de l'entreprise"
                      data-testid="contact-input-company"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="email" className="text-slate-700 mb-2 block">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="votre@email.com"
                      data-testid="contact-input-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-slate-700 mb-2 block">
                      Téléphone
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+241 XX XX XX XX"
                      data-testid="contact-input-phone"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="service" className="text-slate-700 mb-2 block">
                    Type de prestation *
                  </Label>
                  <select
                    id="service"
                    name="service"
                    required
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
                    data-testid="contact-select-service"
                  >
                    <option value="">— Sélectionnez —</option>
                    <option value="transport">
                      Transport de matières premières
                    </option>
                    <option value="location">Location d'engins lourds</option>
                    <option value="both">Transport + Location</option>
                    <option value="logistique">
                      Prestations logistiques clé en main
                    </option>
                    <option value="autre">Autre demande</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="message" className="text-slate-700 mb-2 block">
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    placeholder="Décrivez votre projet (tonnage, axes, durée, type d'engins…)"
                    data-testid="contact-input-message"
                  />
                </div>

                <div className="flex items-start gap-3 bg-orange-50 border border-orange-200 p-4 rounded-lg">
                  <Checkbox
                    id="needsAttachments"
                    checked={formData.needsAttachments}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({
                        ...prev,
                        needsAttachments: checked === true
                      }))
                    }
                    className="mt-1"
                    data-testid="contact-checkbox-attachments"
                  />
                  <div>
                    <Label
                      htmlFor="needsAttachments"
                      className="text-slate-800 font-medium cursor-pointer flex items-center gap-2"
                    >
                      <Paperclip size={16} className="text-orange-500" />
                      Je souhaite joindre des pièces (cahier des charges, plan,
                      etc.)
                    </Label>
                    <p className="text-xs text-slate-600 mt-1">
                      Vous pourrez attacher vos fichiers directement depuis votre
                      messagerie une fois ouverte.
                    </p>
                  </div>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  data-testid="contact-submit-btn"
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white py-6 text-lg rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <Send size={20} className="mr-2" />
                  Envoyer ma demande
                </Button>
                <p className="text-xs text-slate-500 text-center">
                  Le formulaire ouvre votre messagerie avec le message
                  pré-rempli — destinataire : {companyInfo.quoteEmail}
                </p>
              </form>
            </div>

            {/* Carte + intervention urgente */}
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Trouvez-<span className="text-orange-500">nous</span>
              </h2>
              <p className="text-slate-600 mb-6">
                Siège logistique en Zone Industrielle d'Oloumi, Libreville.
                Couverture opérationnelle sur tout le Gabon.
              </p>

              {/* OpenStreetMap */}
              <div className="bg-slate-200 h-80 rounded-lg mb-6 overflow-hidden shadow-md">
                <iframe
                  title="InfraOne — Localisation Libreville"
                  src="https://www.openstreetmap.org/export/embed.html?bbox=9.4039%2C0.3662%2C9.5039%2C0.4662&layer=mapnik&marker=0.4162%2C9.4539"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                ></iframe>
              </div>

              <Card className="bg-orange-50 border-orange-200">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold text-slate-900 mb-4">
                    Intervention urgente ?
                  </h3>
                  <p className="text-slate-700 mb-4">
                    Pour les besoins urgents (dépannage, convoi prioritaire),
                    contactez directement notre cellule d'astreinte.
                  </p>
                  <div className="space-y-3">
                    <a
                      href="tel:+24174754771"
                      className="block bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 text-center"
                    >
                      {companyInfo.phone}
                    </a>
                    <a
                      href="tel:+24160456990"
                      className="block bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 text-center"
                    >
                      {companyInfo.phone2}
                    </a>
                    <a
                      href="tel:+24165300284"
                      className="block bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 text-center"
                    >
                      {companyInfo.phone3}
                    </a>
                    <a
                      href="tel:+33669448351"
                      className="block bg-slate-700 hover:bg-slate-800 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 text-center"
                    >
                      {companyInfo.phoneInternational}
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
